import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useAdminCreateCollectibleVariant,
  useAdminGetAllElements,
  useAdminGetAllVariantsByCollectibleId,
  useAdminUpdateCollectibleVariant,
} from '@outdoria/api';
import { Element, ItemCollectibleVariant } from '@outdoria/types';
import { addCdnUrl, Button } from '@outdoria/ui';
import { Form } from '../../../components';
import { useForm } from '../../../hooks';
import { ImageCreator } from './ImageCreator';

import s from '../ItemUpsertView.module.scss';

type ItemUpsertViewProps = {
  itemId: string | null;
  shouldSave: boolean;
  shouldReset: boolean;
};
export const FormCollectible = ({
  itemId,
  shouldSave,
  shouldReset,
}: ItemUpsertViewProps) => {
  const { data: collectibleVariants } =
    useAdminGetAllVariantsByCollectibleId(itemId);
  const [variants, setVariants] =
    useState<
      (ItemCollectibleVariant & { elements: Element['element_id'][] })[]
    >(collectibleVariants);

  const onAddVariant = () => {
    const defaultVariant = {
      item_collectible_variant_id: '',
      item_id: itemId,
      chance_weight: 0,
      shape: '',
      image: '/items/',
      image_eq: '/items/',
      is_rotable: true,
      thumbnail: '/items/',
      elements: [],
    };

    setVariants(prev => {
      return [...prev, defaultVariant];
    });
  };

  return (
    <>
      <Button
        onClick={onAddVariant}
        variant={Button.Variant.ACTION}
        label="Add a new variant"
      />

      {variants.map((variant, index) => {
        const { elements, ...defaultVariant } = variant;
        return (
          <CollectibleVariant
            key={index}
            index={index}
            itemId={itemId}
            shouldSave={shouldSave}
            shouldReset={shouldReset}
            defaultVariant={defaultVariant}
            defaultElements={elements}
          />
        );
      })}
    </>
  );
};

type CollectibleVariantProps = {
  index: number;
  itemId: string | null;
  shouldSave: boolean;
  shouldReset: boolean;
  defaultVariant: ItemCollectibleVariant;
  defaultElements: Element['element_id'][];
};
const CollectibleVariant = ({
  index,
  itemId,
  shouldSave,
  shouldReset,
  defaultVariant,
  defaultElements,
}: CollectibleVariantProps) => {
  const { register, form, onReset } =
    useForm<Omit<ItemCollectibleVariant, 'item_collectible_variant_id'>>(
      defaultVariant,
    );

  const { elementsOptions } = useOptions();

  const [elements, setElements] =
    useState<Element['element_id'][]>(defaultElements);

  const { mutate: mutateCreate } = useCreate();
  const { mutate: mutateUpdate } = useUpdate(
    defaultVariant.item_collectible_variant_id,
  );

  // Resetting those does not work
  useEffect(() => {
    if (shouldReset) {
      onReset();
    }
  }, [shouldReset]);

  useEffect(() => {
    if (shouldSave && itemId) {
      const { setValue } = register('item_id', { required: true });
      setValue(itemId);
      const formWithId = { ...form, item_id: itemId };

      // If variant has its ID then it means it already existed before
      if (defaultVariant.item_collectible_variant_id) {
        mutateUpdate({ variant: formWithId, elements });
      } else {
        // If not, it is just being created for the first time
        mutateCreate({ variant: formWithId, elements });
      }
    }
  }, [shouldSave, itemId]);

  return (
    <Form title={`Variant #${index}`} pretty>
      <Form.Input
        label="Item ID"
        disabled
        {...register('item_id', { required: true })}
      />

      <Form.Input
        label="Chance weight"
        type="number"
        min={0}
        max={100}
        step={0.01}
        {...register('chance_weight', { required: true })}
      />

      <Form.CheckboxGroup
        label="Elements"
        name="elements"
        setValue={setElements}
        value={elements}
        data={elementsOptions}
      />

      {/* @ts-expect-error TODO I can't be bothered with those types */}
      <ImageCreator form={form} register={register} />
    </Form>
  );
};

/**
 * Get all the form options for the Item Collectible Variant form
 */
const useOptions = () => {
  const { data: elements } = useAdminGetAllElements();

  const elementsOptions = elements.map(element => ({
    value: element.element_id,
    label: <img className={s.element} src={addCdnUrl(element.image)} />,
  }));

  return { elementsOptions };
};

/**
 * Hook for handling creating an Item Collectible Variant
 */
const useCreate = () => {
  const { mutate, error, isSuccess } = useAdminCreateCollectibleVariant();

  useEffect(() => {
    if (error) toast.error(error?.message ?? 'Something went wrong :c');
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Yay! Collectible variant created :3`);
    }
  }, [isSuccess]);

  return { mutate };
};

/**
 * Hook for handling updating the Item Colelctible Variant
 */
const useUpdate = (item_collectible_variant_id: string) => {
  const { mutate, error, isSuccess } = useAdminUpdateCollectibleVariant(
    item_collectible_variant_id,
  );

  useEffect(() => {
    if (error) toast.error(error?.message ?? 'Something went wrong :c');
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Yay! Collectible variant updated :3`);
    }
  }, [isSuccess]);

  return { mutate };
};
